import { FlowParamsContext } from '@/app/FlowInitializer';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppSelectField } from '@/form/FormUI';
import type { NodeFormProps } from '@/nodes';
import type { ExitNodeType } from '@/nodes/types';
import { useSavedFormValues } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { ExitDataSchema, type ExitDataType, getGuideType } from '@rock/types';
import { type FC, useCallback, useContext, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof ExitDataSchema>;

export const ExitForm: FC<NodeFormProps<ExitNodeType>> = ({ flowId, node, updateNode, cancelUpdate }) => {
    const params = useContext(FlowParamsContext);
    const isEquipmentGuide = getGuideType(params) === 'equipment';
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, ExitDataType>({
        resolver: zodResolver(ExitDataSchema),
        // Not seeded with an explicit 'summary' — an unset field should stay unset, so a node that
        // never chose keeps reading the default rather than pinning it on the first save.
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: ExitDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <div className="text-gray-700">This is an exit point to the guide</div>

                    {isEquipmentGuide ? (
                        <div className="text-sm text-gray-500">
                            Equipment guides have no Wrap Up screen — they finish immediately behind a spinner, so there
                            is nothing to choose here.
                        </div>
                    ) : (
                        <AppSelectField
                            control={form.control}
                            name="wrapUp"
                            label="Wrap Up Screen"
                            placeholder="Wrap Up summary (default)"
                            options={[
                                { label: 'Wrap Up summary + Finish button', value: 'summary' },
                                { label: 'Skip — finish immediately', value: 'skip' },
                            ]}
                            description={
                                'Skipping also removes "Add Others", which the Wrap Up is the only place to offer. ' +
                                'An exit inside a sub-guide returns to its parent instead of finishing, so it never ' +
                                'reads this.'
                            }
                        />
                    )}
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};
